import { Box, Divider, Stack, Typography } from '@mui/material';
import { useSession } from '@side/next-auth/react';
import { Chip, useIsMobile } from '@side/pantry';
import { useRef } from 'react';
import { FullSizeFlexContainer } from '@/components/FullSizeFlexContainer';
import { Spinner } from '@/components/Spinner';
import { contentContainerSx } from '@/theme/contentContainerSx';
import { useIdentity } from '$api-clients/hooks';
import { GlossaryButton } from '../components/GlossaryButton/GlossaryButton';
import { ViewAsButton } from '../components/ViewAsButton';
import { useReportsConfiguration } from '../ReportsConfiguration/useReportsConfiguration';
import { StickyHeader } from './components/StickyHeader';

export function ReportContainer({
  viewReportAs,
  title,
  subtitle,
  boldedSubtitle,
  children,
  isEmbedded,
  extra,
  scrollingHeaderExtra,
  forceNarrowActionLayout = false,
  glossaryHiddenCategories,
}: {
  viewReportAs: string;
  title: string;
  subtitle: string;
  boldedSubtitle?: string;
  children: React.ReactNode;
  isEmbedded: boolean;
  extra?: React.ReactNode;
  scrollingHeaderExtra?: React.ReactNode;
  forceNarrowActionLayout?: boolean;
  glossaryHiddenCategories?: string[];
}) {
  const { isLoading: isLoadingUser } = useIdentity(
    '/users/{userId}',
    viewReportAs ? { params: { path: { userId: viewReportAs } } } : null,
  );
  const isMobile = useIsMobile('md');
  // We need to load the config and wait for it to load before we can start
  // requesting other reporting data from inner widgets. This is to help backend
  // ensure we cache identity requests that are used across reporting endpoints.
  const { isLoading: isLoadingConfig } = useReportsConfiguration();
  const { status } = useSession();
  const isLoading = status === 'loading' || isLoadingUser || isLoadingConfig;

  const topRef = useRef<HTMLDivElement>(null);
  const subtitleSentinelRef = useRef<HTMLDivElement>(null);

  if (isLoading) {
    return <Spinner fullScreen />;
  }

  return (
    <FullSizeFlexContainer
      sx={{
        backgroundColor: 'background.default',
        minHeight: isEmbedded ? '100vh' : 0,
        flex: 1,
        borderTop: '1px solid',
        borderColor: 'other.outlineBorder',
        pb: { xs: 6, md: 4.5 },
      }}
    >
      <div ref={topRef} />
      {scrollingHeaderExtra !== undefined && (
        <StickyHeader
          title={title}
          scrollingHeaderExtra={
            <>
              {scrollingHeaderExtra}
              <GlossaryButton
                showOnlyIcon={isMobile}
                size="small"
                hiddenCategories={glossaryHiddenCategories}
              />
            </>
          }
          topRef={topRef}
          subtitleSentinelRef={subtitleSentinelRef}
        />
      )}
      <Stack sx={{ pt: { xs: 2, md: 3 }, ...contentContainerSx }}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          sx={{
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', lg: 'flex-end' },
            gap: 2,
          }}
        >
          <Stack
            sx={{
              gap: 1.25,
              minWidth: 0,
              flex: { lg: '1 1 auto' },
              width: { xs: '100%', lg: 'auto' },
            }}
            ref={subtitleSentinelRef}
          >
            <Typography
              variant="h4"
              component="h2"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              {title}
              <Chip
                label="New"
                size="xs"
                color="secondary"
                variant="outlined"
              />
              <Box
                id="pendo-reports-feedback-container"
                sx={{ minWidth: 200, height: 30 }}
              />
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {subtitle}
              <Typography
                variant="subtitle2"
                component="span"
                sx={{ pl: 0.5, color: 'text.secondary' }}
              >
                {boldedSubtitle}
              </Typography>
            </Typography>
          </Stack>
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              gap: { xs: 1, md: 1.5, lg: 2 },
              width: { xs: '100%', lg: 'auto' },
              flexWrap: 'nowrap',
              justifyContent: 'flex-end',
              minWidth: 0,
              flexShrink: 0,
            }}
          >
            {extra}
            <ViewAsButton
              size="medium"
              forceIconOnly={forceNarrowActionLayout}
            />
          </Stack>
        </Stack>
        <Divider sx={{ my: { xs: 2, md: 4.5 } }} />
        {children}
      </Stack>
    </FullSizeFlexContainer>
  );
}
